import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzUploadFile, NzUploadModule } from 'ng-zorro-antd/upload';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { environment } from '@environment';

@Component({
  selector: 'rappider-file-upload-input',
  standalone: true,
  imports: [
    CommonModule,
    NzButtonModule,
    NzUploadModule,
    NzToolTipModule
  ],
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RappiderFileUploadComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RappiderFileUploadComponent implements ControlValueAccessor {

  @Input() config? = {
    acceptedFileTypes: '.csv, .txt, .json, .png, .xls', // Default accepted file types
    uploadText: 'Click or drag a file to this area to upload',
    maxFileSize: environment.maxFileSize ?? 102400
  };

  // The list of files to be displayed in the nz-upload component.
  fileList: NzUploadFile[] = [];
  isDisabled = false;

  // Callback function to inform the parent form of a value change.
  onChange: (value: File | null) => void = () => { };
  // Callback function to inform the parent form that the component has been touched.
  onTouched: () => void = () => { };

  /**
   * Writes a new value from the form model into the component.
   * @param value The new value (File object or null).
   */
  writeValue(value: File | null): void {
    if (value instanceof File) {
      // If a file is provided, create an NzUploadFile object to display it.
      this.fileList = [{
        uid: '1',
        name: value.name,
        status: 'done',
        size: value.size,
        type: value.type,
        originFileObj: value // Store the original File object
      }];
    } else {
      // If null is passed, clear the file list.
      this.fileList = [];
    }
  }

  /**
   * Registers a callback function to be called when the control's value changes.
   */
  registerOnChange(fn: (value: File | null) => void): void {
    this.onChange = fn;
  }

  /**
   * Registers a callback function to be called when the control is touched.
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * Sets the disabled state of the control.
   */
  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  /**
   * Handles the file selection. Returns false to prevent ng-zorro's default upload behavior.
   * @param file The selected NzUploadFile.
   */
  beforeUpload = (file: NzUploadFile): boolean => {
    if (this.config?.maxFileSize && file.size && file.size > this.config.maxFileSize * 1024) {
      return false;
    }
    // Display the selected file in the list.
    this.fileList = [file];
    // Propagate the raw File object to the parent form.
    this.onChange(file as any as File);
    // Mark the control as touched.
    this.onTouched();
    // Prevent automatic upload.
    return false;
  };

  /**
   * Handles the removal of a file.
   * @param file The file to remove.
   */
  handleRemove = (file: NzUploadFile): boolean => {
    // Clear the displayed file list.
    this.fileList = [];
    // Inform the parent form that the value is now null.
    this.onChange(null);
    // Mark the control as touched.
    this.onTouched();
    return true;
  };

  /**
   * Formats file size from KB to readable format
   * @param sizeInKB File size in KB
   * @returns Formatted file size string
   */
  formatFileSizeForTooltip(sizeInKB: number): string {
    if (sizeInKB >= 1024) {
      return `${(sizeInKB / 1024).toFixed(0)} MB`;
    }
    return `${sizeInKB} KB`;
  }

  /**
   * Gets tooltip text for upload area
   * @returns Tooltip text with file size limit
   */
  getTooltipText(): string {
    const maxSize = this.config?.maxFileSize ?? environment.maxFileSize ?? 102400;
    return `Maximum file size: ${this.formatFileSizeForTooltip(maxSize)}`;
  }

}
